import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CaseDetails, SelectionOption, StiiraError } from '@core/models';
import { CalcHoursWorkedDialog, CalcHoursWorkedSource, PostCalcHoursWorked, PostCalcHoursWorkedRes } from '@core/models/leave-admin/post-calc-hours.model';
import { ErrorService, LayoutService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { Subject, Observable } from 'rxjs';

export enum CalcHoursWorkedMode {
  AvgWeeklyHrs = 1,
  TwelveMonHrsWorked = 2,
  AvgWorkdays = 3,
  All = 4
}

export interface CalcHoursWorkedDialogComponentData {
  caseId: number;
  mode: CalcHoursWorkedMode;
  dialog: CalcHoursWorkedDialog;
  sysText: any;
}

@Component({
    selector: 'app-calc-hours-worked-dialog',
    templateUrl: './calc-hours-worked-dialog.component.html',
    styleUrl: './calc-hours-worked-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CalcHoursWorkedDialogComponent {
  static readonly componentName = 'CalcHoursWorkedDialogComponent';
  
  public position = position;
  public onDragEnded = onDragEnded;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public calcDetails: string[] = null;
  public modes = CalcHoursWorkedMode;
  public cantCalc: boolean = false;
  public missingDetails: string[] = null;
  public form: UntypedFormGroup;
  public sourceOptions: SelectionOption[];

  public isMobile$: Observable<boolean>;

  public get CalcMessage() {
    var message;
    
    if (this.data.mode == this.modes.TwelveMonHrsWorked) {
      if (this.data.dialog.autoUseHoursWorked && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcTwelveMonthsFromWorkMessage;
      else if (this.data.dialog.hasWorkSchedules && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcTwelveMonthsMessage;
      else if (this.data.dialog.hasWorkSchedules)
        message = this.data.sysText.caseLeaveInfo_calcTwelveMonthsFromScheduleMessage;
      else if (this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcTwelveMonthsFromWorkMessage;
    }
    else if (this.data.mode == this.modes.AvgWeeklyHrs) {
      if (this.data.dialog.autoUseHoursWorked && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgHoursFromWorkMessage;
      else if (this.data.dialog.hasWorkSchedules && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgHoursMessage;
      else if (this.data.dialog.hasWorkSchedules)
        message = this.data.sysText.caseLeaveInfo_calcAvgHoursFromScheduleMessage;
      else if (this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgHoursFromWorkMessage;
    }
    else if (this.data.mode == this.modes.AvgWorkdays) {
      if (this.data.dialog.autoUseHoursWorked && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgWorkdaysFromWorkMessage;
      else if (this.data.dialog.hasWorkSchedules && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgWorkdaysMessage;
      else if (this.data.dialog.hasWorkSchedules)
        message = this.data.sysText.caseLeaveInfo_calcAvgWorkdaysFromScheduleMessage;
      else if (this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAvgWorkdaysFromWorkMessage;
    }
    else if (this.data.mode == this.modes.All) {
      if (this.data.dialog.autoUseHoursWorked && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAllFromWorkMessage;
      else if (this.data.dialog.hasWorkSchedules && this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAllMessage;
      else if (this.data.dialog.hasWorkSchedules)
        message = this.data.sysText.caseLeaveInfo_calcAllFromScheduleMessage;
      else if (this.data.dialog.hasWorkHours)
        message = this.data.sysText.caseLeaveInfo_calcAllFromWorkMessage;
    }

    return message;
  }

  constructor(@Inject(MAT_DIALOG_DATA)
    public data: CalcHoursWorkedDialogComponentData,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<CalcHoursWorkedDialogComponent>,
    private leaveAdminService: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private fb: FormBuilder,
    private errorService: ErrorService
  ) {
    this.form = this.fb.group({
      source: this.fb.control<CalcHoursWorkedSource>(null)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close());

    if (this.data.dialog.showSourceOption) {
      this.buildSourceOptions();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public submit(confirm: boolean = false): void {
    const dto: PostCalcHoursWorked = {
      confirm: confirm,
      mode: this.data.mode,
      caseId: this.data.caseId,
      source: this.data.dialog.showSourceOption
        ? this.form.controls.source.value
        : (this.data.dialog.autoUseHoursWorked && this.data.dialog.hasWorkHours) || this.data.dialog.hasWorkHours
          ? CalcHoursWorkedSource.hoursWorked
          : CalcHoursWorkedSource.workSchedule
    }
    this.leaveAdminService.postCalcHoursWorked(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        if ((res as PostCalcHoursWorkedRes)?.calcMissingDetails) {
          this.cantCalc = true;
          this.missingDetails = (res as PostCalcHoursWorkedRes)?.calcMissingDetails;
        } else if (!confirm) {
          this.calcDetails = (res as PostCalcHoursWorkedRes)?.calcDetails;
        } else {
          this.store.caseDetails = res as CaseDetails;
          this.dialogRef.close();
        }
      },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  private buildSourceOptions(): void {
    this.sourceOptions = [];
    const calcHoursWorkedSource = CalcHoursWorkedSource;
    const ids = Object.values(calcHoursWorkedSource).filter(v => typeof v === 'number');
    ids.forEach(id => {
      const desc: string = this.data.sysText['caseLeaveInfo_sourceOption_' + id]
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      this.sourceOptions.push(option);
    });
  }
}
